import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { REGIONS, ALL_LOCATIONS } from "@/lib/locations";
import { useLocation as useRouterLocation } from "react-router-dom";

export const RecentBookingSocialProof = () => {
  const routerLocation = useRouterLocation();
  const path = routerLocation.pathname;
  const shownRef = useRef(false);

  const isEs = path.startsWith('/es');
  const isRu = path.startsWith('/ru');
  const isZh = path.startsWith('/zh');

  useEffect(() => {
    if (path.startsWith('/lp/')) return;

    const showNotification = () => {
      let regionSlug = "sacramento-area";
      const pathParts = path.split('/').filter(Boolean);
      const lastPart = pathParts[pathParts.length - 1];

      if (path.includes('/los-angeles') || path.includes('/la/')) regionSlug = "los-angeles";
      else if (path.includes('/san-francisco')) regionSlug = "san-francisco";
      else if (path.includes('/san-diego')) regionSlug = "san-diego";
      else if (path.includes('/orange-county') || path.includes('/irvine') || path.includes('/anaheim') || path.includes('/huntington-beach') || path.includes('/santa-ana')) regionSlug = "orange-county";
      else if (path.includes('/santa-clara') || path.includes('/san-jose') || path.includes('/silicon-valley')) regionSlug = "santa-clara";
      else if (path.includes('/alameda') || path.includes('/oakland') || path.includes('/berkeley') || path.includes('/fremont') || path.includes('/hayward') || path.includes('/pleasanton') || path.includes('/dublin') || path.includes('/livermore') || path.includes('/tri-valley') || path.includes('/east-bay')) regionSlug = "alameda";
      else if (path.includes('/contra-costa') || path.includes('/walnut-creek') || path.includes('/san-ramon')) regionSlug = "contra-costa";
      else if (path.includes('/marin')) regionSlug = "marin";
      else if (path.includes('/solano') || path.includes('/vallejo') || path.includes('/fairfield') || path.includes('/vacaville') || path.includes('/benicia')) regionSlug = "solano";
      else {
         const loc = ALL_LOCATIONS.find(l => l.slug === lastPart);
         if (loc) regionSlug = loc.regionSlug;
      }

      const region = REGIONS[regionSlug] || REGIONS["sacramento-area"];
      const cities = region.cities;
      const city = cities[Math.floor(Math.random() * cities.length)];

      const servicesEn = ["Standard Clean", "Deep Clean", "Move-In Clean", "Weekly Service", "Bi-Weekly Service"];
      const servicesEs = ["Limpieza Estándar", "Limpieza Profunda", "Limpieza de Mudanza", "Servicio Semanal", "Servicio Quincenal"];
      const servicesRu = ["Стандартная уборка", "Генеральная уборка", "Уборка при переезде", "Еженедельная уборка", "Раз в две недели"];
      const servicesZh = ["标准清洁", "深度清洁", "搬家清洁", "每周服务", "每两周服务"];

      const timesEn = ["just now", "2 minutes ago", "5 minutes ago", "12 minutes ago", "24 minutes ago", "1 hour ago"];
      const timesEs = ["justo ahora", "hace 2 minutos", "hace 5 minutos", "hace 12 minutos", "hace 24 minutos", "hace 1 hora"];
      const timesRu = ["только что", "2 минуты назад", "5 минут назад", "12 минут назад", "24 минуты назад", "1 час назад"];
      const timesZh = ["刚刚", "2分钟前", "5分钟前", "12分钟前", "24分钟前", "1小时前"];

      const services = isEs ? servicesEs : isRu ? servicesRu : isZh ? servicesZh : servicesEn;
      const times = isEs ? timesEs : isRu ? timesRu : isZh ? timesZh : timesEn;

      const service = services[Math.floor(Math.random() * services.length)];
      const time = times[Math.floor(Math.random() * times.length)];

      const title = isZh ? "新预订！" : isRu ? "Новое бронирование!" : isEs ? "¡Nueva Reserva!" : "New Booking!";
      
      const desc = isZh ? (
        <>有人在 <span className="font-semibold text-foreground">{city}</span> 预订了 <span className="font-semibold text-foreground">{service}</span> ({time})。</>
      ) : isRu ? (
        <>Кто-то в <span className="font-semibold text-foreground">{city}</span> заказал <span className="font-semibold text-foreground">{service}</span> {time}.</>
      ) : isEs ? (
        <>Alguien en <span className="font-semibold text-foreground">{city}</span> acaba de reservar <span className="font-semibold text-foreground">{service}</span> {time}.</>
      ) : (
        <>Someone in <span className="font-semibold text-foreground">{city}</span> just booked a <span className="font-semibold text-foreground">{service}</span> {time}.</>
      );

      toast(
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 p-2 rounded-full shrink-0">
            <CheckCircle2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <p className="text-sm font-medium">{title}</p>
            <p className="text-xs text-muted-foreground">{desc}</p>
          </div>
        </div>,
        { duration: 5000, position: "bottom-left" }
      );
    };

    // First notification after 15s (well after LCP + page load)
    const initialDelay = setTimeout(() => {
      if (!shownRef.current) {
        shownRef.current = true;
        showNotification();
      }
    }, 15000);

    // Subsequent notifications every 45–90 seconds
    const interval = setInterval(() => {
      showNotification();
    }, Math.floor(Math.random() * 45000) + 45000);

    return () => {
      clearTimeout(initialDelay);
      clearInterval(interval);
    };
  }, [path, isEs, isRu, isZh]);

  return null;
};
